// Asking to call an order off before it is sent — ordered by mistake, changed
// one's mind — and the seller's answer: accepted (canceled and refunded) or
// declined (sent as it is). One request is open at a time, and it can be taken
// back while the seller has not answered. Once the order is on its way the
// buyer asks for a return instead (ReturnRequests).
import React, { useState } from "react"
import Button from "aldoo-ra/Button"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showErrorToast, showSuccessToast } from "aldoo-ra/Toast"
import useT from "../hooks/useT"
import useSiteLanguage from "../hooks/useSiteLanguage"
import { CancelOrderRequest } from "../storeApi"

// the reasons the API takes (aldoo-na/store/cancellations REASONS), as the buyer reads them
const REASONS = [
  ["ordered_by_mistake", "I ordered it by mistake"],
  ["changed_mind", "I changed my mind"],
  ["found_cheaper", "I found it cheaper elsewhere"],
  ["delivery_too_slow", "The delivery takes too long"],
  ["other", "Another reason"],
]
const REASON_LABELS = Object.fromEntries(REASONS)

// what the seller's screen once put in as the accept message: it said again what the
// headline above it says, so it is not shown
const SAID_ALREADY = new Set(["Your order is canceled and the money is on its way back to you.", "Поръчката Ви е отменена и парите се връщат към Вас."])

const STATUS = {
  open: { label: "Waiting for the seller", cls: "bg-[#ff9800]/15 text-[#a35f00]" },
  accepted: { label: "Accepted", cls: "bg-[#4caf50] text-white" },
  declined: { label: "Declined", cls: "bg-[#f44336]/15 text-[#c62828]" },
  canceled: { label: "Withdrawn", cls: "bg-neutral text-text_secondary" },
}

const when = (value, language) => {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ""
  try {
    return new Intl.DateTimeFormat(language || "en", { day: "numeric", month: language === "en" ? "short" : "long", hour: "2-digit", minute: "2-digit" }).format(d)
  } catch {
    return d.toLocaleString()
  }
}

/** `order`: the buyer's order with its `cancellation` ({ canRequest, requests, unseen }); `onChanged` reloads it. */
export default function CancelRequest({ order, onChanged }) {
  const t = useT()
  const language = useSiteLanguage()
  const cancellation = order.cancellation || { canRequest: false, requests: [] }
  const [reason, setReason] = useState("")
  const [note, setNote] = useState("")
  const [sending, setSending] = useState(false)
  const options = REASONS.map(([id, label]) => ({ id, value: id, name: t(label) }))

  const send = async () => {
    if (!reason) return showErrorToast(t`Choose a reason`)
    setSending(true)
    try {
      const res = await CancelOrderRequest({ op: "create", order_id: order._id, reason, note })
      if (res?.error) return showErrorToast(res.message || t`The request could not be sent`)
      showSuccessToast(t`Your request was sent to the seller`)
      setReason("")
      setNote("")
      onChanged?.()
    } finally {
      setSending(false)
    }
  }

  const withdraw = (request) =>
    showPromptDialog({
      title: t`Withdraw the cancellation request?`,
      message: t`The order stays as it is and will be sent to you.`,
      confirmText: t`Withdraw`,
      cancelText: t`Keep it`,
      onConfirm: async () => {
        const res = await CancelOrderRequest({ op: "withdraw", order_id: order._id, request_id: request._id })
        if (res?.error) return showErrorToast(res.message || t`The request could not be withdrawn`)
        onChanged?.()
      },
    })

  return (
    <div className="flex flex-col gap-4" data-testid="order-cancellation">
      {cancellation.canRequest && (
        <div className="flex flex-col gap-3" data-testid="cancel-request-form">
          <p className="m-0 text-[14px] text-text_secondary">{t`The order has not been sent yet. Ask the seller to cancel it: when they accept, you get your money back.`}</p>
          <label className="flex flex-col gap-1">
            <span className="text-[14px] font-semibold">{t`Reason for the cancellation`}</span>
            <div data-testid="cancel-request-reason">
              <DropdownSelect
                variant="aldoo"
                placeholder={t`Choose a reason`}
                options={options}
                value={options.filter((option) => option.id === reason)}
                onChange={(value) => {
                  const first = Array.isArray(value) ? value[0] : value
                  if (first) setReason(first.id)
                }}
                searchEnabled={false}
                showCheckbox={false}
                showSelectionTick
                enableResetButton={false}
                allowEmptySelection={false}
              />
            </div>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[14px] font-semibold">{t`Note (optional)`}</span>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t`Tell the seller more`}
              className="px-3 py-2 rounded-lg border border-bordered bg-paper text-[15px] text-text outline-none resize-y focus:border-text"
              data-testid="cancel-request-note"
            />
          </label>
          <div className="flex justify-end">
            <Button variant="primary_s" className="min-w-0" disabled={sending || !reason} onClick={send} data-testid="cancel-request-send">
              {t`Ask to cancel the order`}
            </Button>
          </div>
        </div>
      )}

      {cancellation.requests.length > 0 && (
        <ul className="m-0 p-0 list-none flex flex-col gap-3">
          {cancellation.requests.map((request) => {
            const status = STATUS[request.status] || STATUS.open
            return (
              <li key={request._id} className="rounded-lg border border-bordered p-4 flex flex-col gap-2" data-testid="cancel-request" data-status={request.status}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{t`Cancellation request`}</span>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${status.cls}`}>{t(status.label)}</span>
                  <span className="ml-auto text-[12px] text-text_secondary">{when(request.at, language)}</span>
                </div>
                <div className="text-[14px]">
                  <span className="text-text_secondary">{t`Reason`}: </span>
                  {t(REASON_LABELS[request.reason] || request.reason)}
                </div>
                {request.note && <div className="text-[14px] whitespace-pre-line text-text_secondary">“{request.note}”</div>}
                {(request.status === "accepted" || request.status === "declined") && (
                  <div className={`rounded-md px-3 py-2 text-[14px] ${request.status === "accepted" ? "bg-[#4caf50]/10" : "bg-[#f44336]/10"}`} data-testid="cancel-request-answer">
                    <div className={`font-semibold mb-0.5 ${request.status === "accepted" ? "text-[#2e7d32]" : "text-[#c62828]"}`}>
                      {request.status === "accepted" ? t`The seller canceled the order: the money is on its way back to you` : t`The seller declined: the order will be sent`}
                    </div>
                    {request.message && !SAID_ALREADY.has(request.message.trim()) && <div className="whitespace-pre-line">{request.message}</div>}
                    <div className="mt-1 text-[12px] text-text_secondary">{when(request.resolvedAt, language)}</div>
                  </div>
                )}
                {request.status === "open" && (
                  <div className="flex justify-end">
                    <Button variant="secondary_s" className="min-w-0" onClick={() => withdraw(request)} data-testid="cancel-request-withdraw">
                      {t`Withdraw the request`}
                    </Button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
